import React, { useState } from "react";
import {
  MDBBtn,
  MDBCard,
  MDBCardBody,
  MDBCol,
  MDBCollapse,
  MDBRow,
} from "mdbreact";
import Skeleton from "react-loading-skeleton";
import { useFlexPackages } from "./FlexBenefitsPlanCard";
import FlexBenefitsTpaLinks from "./FlexBenefitsTpaLinks";
import { ReactComponent as FlexBenefitsIcon } from "../../assets/flexbenefits.svg";
import {
  effectiveDateText,
  notAppliesText,
  flexBenefitsDocumentsDescriptionText,
  flexBenefitsEnrollmentIdText,
} from "../../core/constants/strings";
import { FlexPackage } from "../../domain/entity/FlexPackage";

interface FlexBenefitsDocumentCardProps {
  pkg: FlexPackage;
  domId: string;
}

const FlexBenefitsDocumentCard = ({
  pkg,
  domId,
}: FlexBenefitsDocumentCardProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <MDBCard className="mb-3" id={`flexbenefits-documents-${domId}`}>
      <MDBCardBody className="p-0">
        <MDBBtn
          block
          className="btn-white p-3 px-lg-5 z-depth-0 text-align-left text-capitalize text-left rounded-0 rounded-top-left rounded-top-right"
          onClick={() => setIsOpen(!isOpen)}
          id={`flexbenefits-documents-${domId}-toggle`}
        >
          <MDBRow className="align-items-center">
            <MDBCol size="auto" className="d-flex justify-content-center">
              <FlexBenefitsIcon />
            </MDBCol>
            <MDBCol>
              <h4 className="mb-0 font-weight-semi-bold text-primary h4-responsive text-capitalize">
                {pkg.name}
              </h4>
              <p className="mb-0">
                <span>{effectiveDateText}</span>{" "}
                <strong className="font-weight-semi-bold">
                  {pkg.enrollment?.startDate || notAppliesText}
                </strong>{" "}
                <span className="silver-text">/</span>{" "}
                {flexBenefitsEnrollmentIdText}{" "}
                <strong className="font-weight-semi-bold">
                  {pkg.enrollment?.externalId || notAppliesText}
                </strong>
              </p>
            </MDBCol>
            <MDBCol size="auto">
              <i
                className={`fa fa-angle-${
                  isOpen ? "up" : "down"
                } rotate-icon text-primary`}
              />
            </MDBCol>
          </MDBRow>
        </MDBBtn>
      </MDBCardBody>
      <MDBCollapse id={`flexbenefits-documents-${domId}-body`} isOpen={isOpen}>
        <hr className="m-0" />
        <MDBCardBody className="px-3 px-lg-5">
          <p className="mb-3">{flexBenefitsDocumentsDescriptionText}</p>
          <FlexBenefitsTpaLinks idPrefix={`flexbenefits-documents-${domId}`} />
        </MDBCardBody>
      </MDBCollapse>
    </MDBCard>
  );
};

function todayIsoDate(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

/**
 * Mirrors mobile-api's AppDocumentService status derivation (effectiveDate vs. today
 * decides UPCOMING/CURRENT), reduced to the one date FlexEnrollment actually has — no
 * expiration/termination/cancellation field exists on it. "past" is unreachable as a
 * result: a cancelled/deleted flex package is already excluded upstream by the "sold"
 * predicate in pivot-health-service, so one never reaches this comparison at all.
 */
function getFlexPackageStatus(pkg: FlexPackage): "current" | "upcoming" {
  const startDate = pkg.enrollment?.startDate;
  return startDate && startDate > todayIsoDate() ? "upcoming" : "current";
}

/**
 * A package with a name still identifies real coverage even without enrollment data
 * (falls back to N/A on its own two fields, which is fine). One with neither has
 * nothing to show at all -- just a blank title over "N/A / N/A" -- so it's excluded
 * rather than rendered as an empty card.
 */
function hasDisplayableIdentity(pkg: FlexPackage): boolean {
  return Boolean(
    pkg.name || pkg.enrollment?.startDate || pkg.enrollment?.externalId
  );
}

interface FlexBenefitsDocumentGroupProps {
  selected: string;
}

/**
 * FlexBenefits' presence on the Documents page (Slice 3).
 *
 * Link-out only, per the session decision: FlexBenefits documents live on Loomis's
 * site, not in appsDocumentsRepository, so there is no per-document list to fetch —
 * only the same two TPA links FlexBenefitsSupport already uses. Collapsed by default
 * and expandable, matching the other product cards on this page, even though there's
 * no per-document list inside — just the description and the two links.
 *
 * One card per package (not one combined row) because effective date and the
 * enrollment id are package-specific — joining them across packages the way the
 * sidebar sentence does would misattribute one package's id/date to another.
 *
 * Filtered by `selected` (the same Past/Current/Upcoming tab the rest of the page
 * uses) so a package doesn't show up identically under all three tabs.
 *
 * The icon is a placeholder pending a real FlexBenefits design asset.
 */
const FlexBenefitsDocumentGroup = ({
  selected,
}: FlexBenefitsDocumentGroupProps) => {
  const { isLoading, packages } = useFlexPackages();

  if (isLoading) {
    return <Skeleton height="4.56rem" className="mb-3" />;
  }

  const visiblePackages = packages
    .filter(hasDisplayableIdentity)
    .filter((pkg) => getFlexPackageStatus(pkg) === selected);

  if (visiblePackages.length === 0) {
    return null;
  }

  return (
    <>
      {visiblePackages.map((pkg, index) => {
        const domId = pkg.id ?? `${index}`;
        return <FlexBenefitsDocumentCard key={domId} pkg={pkg} domId={domId} />;
      })}
    </>
  );
};

export default FlexBenefitsDocumentGroup;
